// WHAT: Chart component for displaying time tracking insights by category/aspect
// WHY:  Provides visual breakdown of time spent across workspaces/aspects
// DOMAIN: Productivity → TimeTracQ
// DEPENDENCIES: recharts for charting, useFilter hook, core.invoke
// RISKS:
//   - NOTE: Uses mock data as fallback - may show incorrect data
//   - NOTE: Breakdown by categories/aspects toggle may not work correctly
//   - NOTE: Timeframe selection (days/weeks/months) may not be implemented

import { core } from "@tauri-apps/api";
import { useState, useEffect } from "react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer } from "recharts";

import { Card } from "@/components/ui";

import { useFilter } from "./FilterContextProvider.shared";

/**
 * WHAT:
 *   Formats a number of minutes into a human-readable time string for tooltips.
 *
 * @param value - The number of minutes to format
 *
 * @returns {string} Formatted time string (e.g., "2h 30m")
 *
 */
const formatTooltipValue = (value: number): string => {
  const hours = Math.floor(value / 60);
  const minutes = Math.floor(value % 60);
  return `${String(hours)}h ${String(minutes)}m`;
};

/**
 * WHAT:
 *   Formats total minutes into a human-readable time string.
 *
 * @param minutes - The total number of minutes to format
 *
 * @returns {string} Formatted time string (e.g., "2h 30m")
 *
 */
const formatTotalTime = (minutes: number): string => {
  const hours = Math.floor(minutes / 60);
  const mins = Math.floor(minutes % 60);
  return `${String(hours)}h ${String(mins).padStart(2, "0")}m`;
};

/**
 * Daily data interface for chart
 */
interface DailyData {
  /** Workspace names as keys with minutes as values */
  [key: string]: number | string;
  /** Date string */
  date: string;
}

/**
 * Backend response interface
 */
interface BackendResponse {
  /** Category chart data */
  category_chart?: DailyData[];
  /** Total minutes */
  total_minutes?: number;
}

/**
 * WHAT:
 *   Chart component that displays time tracking insights as bar chart with
 *   breakdown by categories or aspects and timeframe selection.
 *
 * WHY:
 *   Enables users to visualize time distribution across workspaces/aspects
 *   over different time periods.
 *
 * BEHAVIOR:
 *   - Fetches aggregated data from backend
 *   - Displays bar chart with workspace/aspect breakdown
 *   - Supports breakdown toggle (categories/aspects)
 *   - Supports timeframe selection (days/weeks/months)
 *   - Falls back to mock data if backend fails
 *
 * RISKS:
 *   - NOTE: Uses mock data as fallback - may show incorrect data
 *   - NOTE: Breakdown by categories/aspects toggle may not work correctly
 *   - NOTE: Timeframe selection (days/weeks/months) may not be implemented
 *
 * @returns {JSX.Element} The insights chart component
 *
 */
export const InsightsChart = () => {
  const { dateFilter } = useFilter();

  const [data, setData] = useState<DailyData[]>([]);
  const [totalMinutes, setTotalMinutes] = useState<number>(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<null | string>(null);

  const [breakdownBy, setBreakdownBy] = useState<"aspects" | "categories">("categories");
  const [timeframe, setTimeframe] = useState<"days" | "months" | "weeks">("days");

  // NOTE: Load data from backend
  useEffect(() => {
    setLoading(true);
    setError(null);
    void (async () => {
      try {
        const res = await core.invoke<BackendResponse>("get_aggregated_summary", {
          filter: dateFilter,
        });

        // NOTE: Use backend data if available, otherwise fall back to mock data
        if (res.category_chart && res.category_chart.length > 0) {
          setData(res.category_chart);
        } else {
          // NOTE: Mock data for testing (should be removed when backend is ready)
          const mockData = [
            { date: "2024-01-01", Investments: 60, Nexus: 120, Trading: 30 },
            { date: "2024-01-02", Investments: 45, Nexus: 90, Trading: 15 },
            { date: "2024-01-03", Investments: 30, Nexus: 150, Trading: 60 },
          ];
          setData(mockData);
        }
        setTotalMinutes(res.total_minutes ?? 0);
      } catch {
        setError("Fehler beim Laden der Daten");
      } finally {
        setLoading(false);
      }
    })();
  }, [dateFilter]);

  // Workspace-Farben definieren
  const workspaceColors = {
    Investments: "#FFFFFF", // Weiß
    Nexus: "#3B82F6", // Blau
    "Real Estate": "#8B5CF6", // Lila
    Reselling: "#F59E0B", // Orange
    TracQ: "#EAB308", // Gelb
    Trading: "#10B981", // Grün
  };

  return (
    <Card
      className="bg-slate-700/30 border-slate-600/50 shadow-tracq-card"
      padding="lg"
      variant="elevated"
    >
      <div className="space-y-6">
        {/* Header mit Total Time und Controls */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-6">
            <div>
              <h3 className="text-2xl font-bold text-white">Time Distribution</h3>
              <p className="text-slate-400 text-sm">Zeitverteilung über den gewählten Zeitraum</p>
            </div>
            <div className="text-right">
              <div className="text-sm text-slate-400">Total Time</div>
              <div className="text-3xl font-bold text-tracq-bright-blue">
                {formatTotalTime(totalMinutes)}
              </div>
            </div>
          </div>

          <div className="flex items-center gap-3">
            {/* Breakdown by Dropdown */}
            <div className="flex items-center gap-2">
              <label className="text-sm text-slate-400">Breakdown by</label>
              <select
                className="bg-slate-700/50 border border-slate-500/50 text-slate-200 text-sm px-3 py-1 rounded-md focus:ring-2 focus:ring-tracq-bright-blue focus:border-tracq-bright-blue transition-all duration-200"
                onChange={(e) => {
                  setBreakdownBy(e.target.value as "aspects" | "categories");
                }}
                value={breakdownBy}
              >
                <option value="categories">Categories</option>
                <option value="aspects">Aspects</option>
              </select>
            </div>

            {/* Timeframe Dropdown */}
            <div className="flex items-center gap-2">
              <label className="text-sm text-slate-400">and</label>
              <select
                className="bg-slate-700/50 border border-slate-500/50 text-slate-200 text-sm px-3 py-1 rounded-md focus:ring-2 focus:ring-tracq-bright-blue focus:border-tracq-bright-blue transition-all duration-200"
                onChange={(e) => {
                  setTimeframe(e.target.value as "days" | "months" | "weeks");
                }}
                value={timeframe}
              >
                <option value="days">Days</option>
                <option value="weeks">Weeks</option>
                <option value="months">Months</option>
              </select>
            </div>
          </div>
        </div>

        {/* Chart */}
        {(() => {
          if (loading) {
            return (
              <div className="h-80 flex items-center justify-center text-slate-500 animate-pulse">
                Loading…
              </div>
            );
          }
          if (error) {
            return (
              <div className="h-80 flex items-center justify-center text-red-400">{error}</div>
            );
          }
          if (data.length === 0) {
            return (
              <div className="h-80 flex items-center justify-center text-slate-300">
                Keine Daten für den gewählten Zeitraum
              </div>
            );
          }
          return (
            <div className="h-80">
              <ResponsiveContainer height="100%" width="100%">
                <BarChart data={data} margin={{ bottom: 60, left: 20, right: 30, top: 20 }}>
                  <XAxis
                    angle={-45}
                    dataKey="date"
                    height={60}
                    textAnchor="end"
                    tick={{ fill: "#94A3B8", fontSize: 12 }}
                  />
                  <YAxis
                    label={{ angle: -90, position: "insideLeft", value: "Hours" }}
                    tick={{ fill: "#94A3B8", fontSize: 12 }}
                  />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: "#1E293B",
                      border: "1px solid #334155",
                      borderRadius: "8px",
                      color: "#F1F5F9",
                    }}
                    formatter={(value: number) => [formatTooltipValue(value), ""]}
                  />
                  {/* Dynamische Bars für jeden Workspace */}
                  {Object.keys(workspaceColors).map((workspace) => (
                    <Bar
                      dataKey={workspace}
                      fill={workspaceColors[workspace as keyof typeof workspaceColors]}
                      isAnimationActive={false}
                      key={workspace}
                      stackId="a"
                    />
                  ))}
                </BarChart>
              </ResponsiveContainer>
            </div>
          );
        })()}

        {/* Legend */}
        <div className="flex flex-wrap gap-4 justify-center">
          {Object.entries(workspaceColors).map(([workspace, color]) => (
            <div className="flex items-center gap-2" key={workspace}>
              <div className="w-3 h-3 rounded-full" style={{ backgroundColor: color }} />
              <span className="text-sm text-slate-300">{workspace}</span>
            </div>
          ))}
        </div>
      </div>
    </Card>
  );
};
